import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { formatCurrencySpaced } from '@/lib/market';
import {
  Wallet, TrendingUp, ArrowUpRight, ArrowDownRight, RefreshCw, Plus, CircleDollarSign, ChevronRight, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

const CHART_PALETTE = ['#ffd879','#d9e2c3','#a8c5da','#c9a86b','#9b8dc4','#8dc4a8','#c4a88d','#8da8c4','#b8c4a8','#c4b88d','#c4c48d','#a8a8c4'];

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (v, market) => formatCurrencySpaced(v, market);

const GainBadge = ({ pct }) => (
  <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
    {pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
    {Math.abs(pct).toFixed(1)}%
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm h-full">
    <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{label}</p>
    <p className="text-lg font-bold text-[#1a1a1a]">{value}</p>
    {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
  </div>
);

function CashTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <Wallet className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No cash & equivalents found</p>
      </div>
    );
  }

  const totalBalance = data.reduce((s, c) => s + (c.balance || 0), 0);
  const avgRate      = data.length > 0 ? (data.reduce((s, c) => s + (c.interest_rate || 0), 0) / data.length).toFixed(2) : '0.00';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Balance"  value={fmt(totalBalance, market)} sub={`${data.length} accounts / instruments`} />
        <StatCard label="Avg. Interest"  value={`${avgRate}%`}            sub="blended rate p.a." />
        <StatCard label="Annual Income"  value={fmt(totalBalance * parseFloat(avgRate) / 100, market)} sub="estimated interest income" />
      </div>

      <div className="border border-[#e5e5e5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#dae4c4]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Account / Instrument</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Institution</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Balance</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Rate</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5] bg-white">
            {data.map((c, i) => (
              <tr key={i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5 text-[#ffd879] shrink-0" />{c.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge className="bg-[#ffd879]/20 text-[#1a1a1a] border-0 text-xs">{c.type}</Badge>
                </td>
                <td className="px-4 py-3 text-[#4a4a4a]">{c.institution}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1a1a1a]">{fmt(c.balance, market)}</td>
                <td className="px-4 py-3 text-right text-emerald-600">{c.interest_rate}%</td>
                <td className="px-4 py-3 text-right text-xs text-[#4a4a4a]">{c.maturity || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FixedDepositsTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <CircleDollarSign className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No fixed deposits found</p>
      </div>
    );
  }

  const totalPrincipal = data.reduce((s, d) => s + (d.current_value || d.avg_price || 0), 0);
  const avgRate = data.length > 0
    ? (data.reduce((s, d) => s + (d.interest_rate || 0), 0) / data.length).toFixed(2)
    : '0.00';
  const annualIncome = data.reduce((s, d) => {
    const principal = d.current_value || d.avg_price || 0;
    return s + (principal * (d.interest_rate || 0) / 100);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Principal"  value={fmt(totalPrincipal, market)} sub={`${data.length} deposit${data.length !== 1 ? 's' : ''}`} />
        <StatCard label="Avg. Interest"    value={`${avgRate}%`}               sub="blended rate p.a." />
        <StatCard label="Annual Income"    value={fmt(annualIncome, market)}   sub="estimated interest income" />
      </div>

      <div className="border border-[#e5e5e5] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#dae4c4]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Account / FD Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Bank / Institution</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Principal</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Rate</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[#1a1a1a]">Maturity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e5e5e5] bg-white">
            {data.map((d, i) => (
              <tr key={i} className="hover:bg-[#f9f9f9] transition-colors">
                <td className="px-4 py-3 font-medium text-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <CircleDollarSign className="w-3.5 h-3.5 text-[#ffd879] shrink-0" />
                    {d.name || d.symbol}
                  </div>
                </td>
                <td className="px-4 py-3 text-[#4a4a4a]">{d.broker || d.portfolio_manager || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold text-[#1a1a1a]">{fmt(d.current_value || d.avg_price, market)}</td>
                <td className="px-4 py-3 text-right text-emerald-600">{d.interest_rate ? `${d.interest_rate}%` : '—'}</td>
                <td className="px-4 py-3 text-right text-xs text-[#4a4a4a]">{d.maturity_date || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlternativesTab({ data, market }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-8 h-8 text-[#4a4a4a] mx-auto mb-2" />
        <p className="text-[#4a4a4a] text-sm">No alternatives found</p>
      </div>
    );
  }

  const totalInvested = data.reduce((s, a) => s + (a.aum_share || 0), 0);
  const totalNav      = data.reduce((s, a) => s + (a.nav || 0), 0);
  const avgYTD        = data.length > 0 ? (data.reduce((s, a) => s + (a.ytd || 0), 0) / data.length).toFixed(1) : '0.0';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Committed" value={fmt(totalInvested, market)} sub={`${data.length} funds`} />
        <StatCard label="Current NAV"     value={fmt(totalNav, market)}      sub="marked to NAV" />
        <StatCard label="Avg. YTD Return" value={`${avgYTD}%`}              sub="across portfolio" />
        <StatCard label="Unrealised Gain" value={fmt(totalNav - totalInvested, market)} sub="NAV vs. cost" />
      </div>

      <div className="space-y-3">
        {data.map((a, i) => (
          <Card key={i} className="bg-white border-[#e5e5e5]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#ffd879]/15 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#ffd879]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a1a] text-sm">{a.name}</p>
                    <p className="text-xs text-[#4a4a4a] mt-0.5">{a.type} · {a.strategy}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right shrink-0">
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Committed</p>
                    <p className="text-sm font-medium text-[#2a2a2a]">{fmt(a.
                      _share, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">NAV</p>
                    <p className="text-sm font-bold text-[#1a1a1a]">{fmt(a.nav, market)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">YTD</p>
                    <GainBadge pct={a.ytd} />
                  </div>
                  <div>
                    <p className="text-xs text-[#4a4a4a]">Since Inc.</p>
                    <GainBadge pct={a.since_inc} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function CashAlternativesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const market = user?.market || 'IN';

  const activeTab = searchParams.get('tab') || 'overview';
  const handleTabChange = (tab) => setSearchParams({ tab });

  const [loading, setLoading] = useState(true);
  const [cash, setCash]                 = useState([]);
  const [alternatives, setAlternatives] = useState([]);
  const [fixedDeposits, setFixedDeposits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      try {
        const [cashRes, altRes, fdRes] = await Promise.all([
          axios.get(`${API}/other-assets/cash`).catch(() => ({ data: [] })),
          axios.get(`${API}/other-assets/alternatives`).catch(() => ({ data: [] })),
          axios.get(`${API}/markets/holdings?asset_class=fd`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCash(cashRes.data || []);
        setAlternatives(altRes.data || []);
        setFixedDeposits(fdRes.data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const cashValue = cash.reduce((s, c) => s + (c.balance || 0), 0);
  const altValue  = alternatives.reduce((s, a) => s + (a.nav || 0), 0);
  const fdValue   = fixedDeposits.reduce((s, d) => s + (d.current_value || d.avg_price || 0), 0);
  const totalAUM  = cashValue + altValue + fdValue;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <RefreshCw className="w-8 h-8 text-[#ffd87a] animate-spin" />
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 46px 72px', background: '#f8f6f1', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 46, height: 46, borderRadius: 13, background: '#d9e2c3', color: '#1a1a1a', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Wallet size={20} />
          </div>
          <div>
            <div style={{ textTransform: 'uppercase', fontSize: 10, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 4 }}>
              Liquid Assets & Alternatives · Total {fmt(totalAUM, market)}
            </div>
            <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 34, letterSpacing: '-0.025em', lineHeight: 1.04, color: '#2a2a2a', margin: 0 }}>
              Cash & Alternatives
            </h1>
          </div>
        </div>
        <button
          onClick={() => navigate('/import')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 18px', borderRadius: 99, background: '#ffd87a', color: '#000', fontWeight: 600, fontSize: 13.5, border: 'none', cursor: 'pointer' }}
        >
          <Plus size={16} /> Add Holdings
        </button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6 h-auto gap-1 p-1 w-fit flex-wrap">
          <TabsTrigger value="overview" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="cash" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Cash & Equivalents
          </TabsTrigger>
          <TabsTrigger value="fd" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <CircleDollarSign className="w-3.5 h-3.5" /> Fixed Deposits
          </TabsTrigger>
          <TabsTrigger value="alternatives" className="text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Alternatives
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Stat band */}
            <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              {[
                { k: 'Total Cash', v: fmt(totalAUM, market), delta: 'Cash · FD · Alternatives' },
                { k: 'Cash & Equivalents', v: fmt(cashValue, market), delta: `${cash.length} accounts` },
                { k: 'Fixed Deposits', v: fmt(fdValue, market), delta: `${fixedDeposits.length} deposits` },
                { k: 'Alternatives', v: fmt(altValue, market), delta: `${alternatives.length} funds` },
              ].map((cell, i) => (
                <div key={i} style={{ flex: 1, minWidth: 130, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: '5px 0 4px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
                  <div style={{ fontSize: 10.5, color: '#888' }}>{cell.delta}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            {totalAUM > 0 && (() => {
              const segs = [
                { name: 'Cash', label: 'Cash & Equivalents', value: cashValue },
                { name: 'Fixed Deps', label: 'Fixed Deposits', value: fdValue },
                { name: 'Alternatives', label: 'Alternatives', value: altValue },
              ].filter(d => d.value > 0).map((d, i) => ({ ...d, color: CHART_PALETTE[i % CHART_PALETTE.length] }));
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'stretch' }}>
                  <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 14 }}>Allocation Breakdown</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={segs} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fontSize: 10.5, fill: '#888' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v} />
                        <Tooltip formatter={(v) => [fmt(v, market), 'Value']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5e5' }} />
                        <Bar dataKey="value" radius={[5,5,0,0]}>
                          {segs.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 12 }}>Portfolio Split</div>
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <PieChart width={150} height={200}>
                          <Pie data={segs.map(d => ({ name: d.label, value: d.value }))} cx={75} cy={100} innerRadius={52} outerRadius={72} dataKey="value" paddingAngle={3}>
                            {segs.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                        </PieChart>
                      </div>
                      <ul style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                        {segs.map((d, i) => {
                          const pct = totalAUM > 0 ? d.value / totalAUM * 100 : 0;
                          return (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 0', borderBottom: i < segs.length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 10.5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                              <span style={{ flex: 1, color: '#555' }}>{d.label}</span>
                              <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{pct < 0.1 ? '<0.1' : pct.toFixed(1)}%</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Quick nav */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { tab: 'cash',         icon: Wallet,           label: 'Cash & Equivalents', value: fmt(cashValue, market),  sub: `${cash.length} accounts` },
                { tab: 'fd',           icon: CircleDollarSign, label: 'Fixed Deposits',     value: fmt(fdValue, market),    sub: `${fixedDeposits.length} deposits` },
                { tab: 'alternatives', icon: TrendingUp,       label: 'Alternatives',       value: fmt(altValue, market),   sub: `${alternatives.length} funds` },
              ].map(({ tab, icon: Icon, label, value, sub }) => (
                <button key={tab} onClick={() => handleTabChange(tab)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
                  background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffd879'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e5e5'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,216,121,0.15)', display: 'grid', placeItems: 'center' }}>
                      <Icon size={16} style={{ color: '#c9a000' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
                      <div style={{ fontSize: 10.5, color: '#888', marginTop: 1 }}>{sub}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{value}</div>
                    <ChevronRight size={13} style={{ color: '#ffd879', marginLeft: 'auto', marginTop: 2 }} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cash">
          <CashTab data={cash} market={market} />
        </TabsContent>
        <TabsContent value="fd">
          <FixedDepositsTab data={fixedDeposits} market={market} />
        </TabsContent>
        <TabsContent value="alternatives">
          <AlternativesTab data={alternatives} market={market} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
